import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Plus,
  Search,
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  Building2,
  Phone,
  CreditCard,
  Users,
  X,
  Loader2,
  Mail,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { EmailSubscriptionsModal } from "../components/EmailSubscriptionsModal";
import {
  BILLING_SOURCE_TIMEZONE,
  convertLocalScheduleToSource,
  getLocalizedScheduleInfo,
  getTimezoneDisplayName,
  getUserTimeZone,
} from "../utils/timezone";

// ── Office Schedule types ────────────────────────────────────────────────────
interface TimeWindow { start: string; end: string; }
type ScheduleMode = "always_on" | "global" | "per_day";
const OFFICE_SCHEDULE_DAY_KEYS = ["monday","tuesday","wednesday","thursday","friday","saturday","sunday"] as const;
type OfficeDayKey = typeof OFFICE_SCHEDULE_DAY_KEYS[number];
const OFFICE_SCHEDULE_DAY_LABELS: Record<OfficeDayKey, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};
interface OfficeSchedule {
  mode: ScheduleMode;
  global: TimeWindow[];
  perDay: Record<OfficeDayKey, TimeWindow[]>;
}
const EMPTY_OFFICE_SCHEDULE: OfficeSchedule = {
  mode: "per_day",
  global: [],
  perDay: { monday:[], tuesday:[], wednesday:[], thursday:[], friday:[], saturday:[], sunday:[] },
};

interface CustomerProfile {
  id: string;
  qboCustomerId?: string;
  genesysQueueNames: string[];
  telephonyQueueKeys?: string[];
  isActive: boolean;
  serviceType: "inbound" | "outbound" | "both" | "ai_admin" | "ai_medical";
  retellAgentId?: string | null;
  ratePerMinute: number;
  reactivationBillDate?: number | null;
  reactivationMonthlyRate?: number | null;
  reactivationPatientsCalledPerMonth?: number | null;
  aiMinimumDollar?: number | null;
  initialPaymentDate?: string | null;
  initialPaymentAmount?: number | null;
  goLiveDate?: string | null;
  billingStatus?: "pending" | "active";
  hasInvoiceDate?: boolean;
  invoiceDate?: number | null;
  billOveragesSeparately?: boolean;
  overageBillDate?: number | null;
  overageBillOffsetDays?: number | null;
  overageHasInvoiceDate?: boolean;
  overageInvoiceDate?: number | null;
  weeklyOveragesEnabled?: boolean;
  weeklyOverageBillOffsetDays?: number | null;
  taxCode?: string;
  notes?: string;
  handledBy?: "NR Admin" | "NR Medical" | null;
  billDate: number;
  billTime: string;
  billingPeriodStart: number;
  billingPeriodEnd: number;
  minimumMinutes: number;
  overageRatePerMinute: number;
  qboDisplayName: string;
  qboCompanyName?: string;
  qboGivenName?: string;
  qboFamilyName?: string;
  qboMiddleName?: string;
  qboTitle?: string;
  qboSuffix?: string;
  qboPrimaryEmailAddr?: string;
  qboPrimaryPhone?: string;
  qboLine1?: string;
  qboCity?: string;
  qboCountrySubDivisionCode?: string;
  qboPostalCode?: string;
  qboCountry?: string;
  qboMetadata?: any;
  includeDisposition?: boolean;
  includeANI?: boolean;
  perCallReport?: boolean;
  reportTimezone?: string;
  officeSchedule?: OfficeSchedule | null;
  forecastStartDate?: string | null;
  contractEndDate?: string | null;
  projectionWindowStart?: number | null;
  projectionWindowEnd?: number | null;
  weeklyEstimates?: {
    monday:    { calls: number; ahtMinutes: number };
    tuesday:   { calls: number; ahtMinutes: number };
    wednesday: { calls: number; ahtMinutes: number };
    thursday:  { calls: number; ahtMinutes: number };
    friday:    { calls: number; ahtMinutes: number };
    saturday:  { calls: number; ahtMinutes: number };
    sunday:    { calls: number; ahtMinutes: number };
  } | null;
  createdAt: string;
  updatedAt: string;
  billingTimezone?: string;
  clientTimezone?: string;
  billDateBilling?: number;
  billTimeBilling?: string;
  billTimeBillingFormatted?: string;
  billTimeFormatted?: string;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

interface CustomerProfileFilters {
  search: string;
  isActive?: boolean;
  hasQboCustomerId?: boolean;
  hasTelephonyQueues?: boolean;
  queueName?: string;
  serviceType?: string;
  handledBy?: string;
  reportTypes?: string[];
}

const REPORT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "minutes_usage", label: "Minutes Usage" },
  { value: "ai_minutes_usage", label: "AI Minutes Usage" },
  { value: "capture_rates", label: "Capture Rates" },
  { value: "phone_log", label: "Phone Log" },
  { value: "kpi_summary", label: "KPI Summary" },
  { value: "kpi_v2", label: "KPI v2" },
  { value: "pcc_performance", label: "PCC Performance" },
  { value: "detailed_performance", label: "Detailed Performance" },
  { value: "inbound_call_log", label: "Inbound Call Log" },
  { value: "special_report", label: "Special Report" },
];

function ReportTypeMultiSelect({
  selected,
  onChange,
}: {
  selected: string[];
  onChange: (values: string[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleValue = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const label =
    selected.length === 0
      ? "All Report Types"
      : selected.length === 1
      ? REPORT_TYPE_OPTIONS.find((o) => o.value === selected[0])?.label ?? "1 selected"
      : `${selected.length} selected`;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center justify-between w-full border border-gray-300 rounded-md shadow-sm px-3 py-2 text-sm bg-white text-left focus:ring-primary-500 focus:border-primary-500"
      >
        <span className={selected.length === 0 ? "text-gray-500" : "text-gray-900"}>{label}</span>
        <span className="text-gray-400 ml-2">▾</span>
      </button>
      {open && (
        <div className="absolute z-30 mt-1 w-56 max-h-64 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg py-1">
          {selected.length > 0 && (
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full text-left px-3 py-1.5 text-xs text-primary-600 hover:bg-gray-50 border-b border-gray-100 mb-1"
            >
              Clear all
            </button>
          )}
          {REPORT_TYPE_OPTIONS.map((opt) => (
            <label
              key={opt.value}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => toggleValue(opt.value)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              {opt.label}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

type CustomerProfileFormData = Omit<
  CustomerProfile,
  "id" | "createdAt" | "updatedAt" | "ratePerMinute"
> & {
  ratePerMinute: string;
};

type CustomerProfilePayload = Omit<
  CustomerProfile,
  "id" | "createdAt" | "updatedAt"
> & {
  clientTimeZone?: string;
};

interface SaveResult {
  success: boolean;
  errorMessage?: string;
}

interface AircallQueueOption {
  id: string;
  name: string;
  description?: string;
}

const AIRCALL_QUEUE_CACHE_TTL_MS = 5 * 60 * 1000;
let aircallQueueCache:
  | { items: AircallQueueOption[]; fetchedAt: number }
  | null = null;
let aircallQueuePromise: Promise<AircallQueueOption[]> | null = null;

const fetchAircallQueues = async (): Promise<AircallQueueOption[]> => {
  const now = Date.now();
  if (
    aircallQueueCache &&
    now - aircallQueueCache.fetchedAt < AIRCALL_QUEUE_CACHE_TTL_MS
  ) {
    return aircallQueueCache.items;
  }

  if (aircallQueuePromise) {
    return aircallQueuePromise;
  }

  aircallQueuePromise = fetch("/api/v1/aircall/queues/all")
    .then(async (response) => {
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}));
        throw new Error(errorBody?.error || "Failed to load Aircall queues");
      }
      return response.json();
    })
    .then((payload) => {
      const items = Array.isArray(payload?.data) ? payload.data : [];
      const sorted = items
        .slice()
        .sort((a: AircallQueueOption, b: AircallQueueOption) =>
          a.name.localeCompare(b.name)
        );
      aircallQueueCache = { items: sorted, fetchedAt: Date.now() };
      return sorted;
    })
    .finally(() => {
      aircallQueuePromise = null;
    });

  return aircallQueuePromise;
};

export function CustomerProfiles() {
  const [customerProfiles, setCustomerProfiles] = useState<CustomerProfile[]>(
    []
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 500,
    total: 0,
    pages: 0,
  });
  const [filters, setFilters] = useState<CustomerProfileFilters>({
    search: "",
    isActive: undefined,
    hasQboCustomerId: undefined,
    hasTelephonyQueues: undefined,
    queueName: undefined,
    serviceType: undefined,
    handledBy: undefined,
  });
  const [showModal, setShowModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState<CustomerProfile | null>(
    null
  );
  const [showStats, setShowStats] = useState<string | null>(null);
  const [showEmailSubscriptions, setShowEmailSubscriptions] = useState<CustomerProfile | null>(null);
  const [expandedNoteIds, setExpandedNoteIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchCustomerProfiles();
  }, [pagination.page, pagination.limit, filters]);

  const fetchCustomerProfiles = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      params.append("clientTimeZone", getUserTimeZone());

      if (filters.search) params.append("search", filters.search);
      if (filters.isActive !== undefined)
        params.append("isActive", filters.isActive.toString());
      if (filters.hasQboCustomerId !== undefined)
        params.append("hasQboCustomerId", filters.hasQboCustomerId.toString());
      if (filters.hasTelephonyQueues !== undefined)
        params.append("hasTelephonyQueues", filters.hasTelephonyQueues.toString());
      if (
        filters.queueName &&
        filters.queueName !== "complete" &&
        filters.queueName !== "partial" &&
        filters.queueName !== "minimal" &&
        filters.queueName !== "active_no_queue"
      ) {
        params.append("queueName", filters.queueName);
      }
      if (filters.reportTypes && filters.reportTypes.length > 0)
        params.append("reportTypes", filters.reportTypes.join(","));

      const response = await fetch(`/api/v1/customer-profiles?${params}`);
      const data = await response.json();

      if (data.success) {
        let filteredProfiles = data.data;

        // Apply profile completeness filter on the frontend
        if (filters.queueName === "complete") {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) => getCompletionScore(p) >= 80
          );
        } else if (filters.queueName === "partial") {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) =>
              getCompletionScore(p) >= 40 && getCompletionScore(p) < 80
          );
        } else if (filters.queueName === "minimal") {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) => getCompletionScore(p) < 40
          );
        } else if (filters.queueName === "active_no_queue") {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) =>
              p.isActive &&
              (!p.genesysQueueNames || p.genesysQueueNames.length === 0) &&
              (!p.telephonyQueueKeys || p.telephonyQueueKeys.length === 0)
          );
        }

        if (filters.serviceType) {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) => p.serviceType === filters.serviceType
          );
        }

        if (filters.handledBy) {
          filteredProfiles = filteredProfiles.filter(
            (p: CustomerProfile) => p.handledBy === filters.handledBy
          );
        }

        setCustomerProfiles(filteredProfiles);
        const isClientSideFiltered = ["complete", "partial", "minimal", "active_no_queue"].includes(filters.queueName ?? "") || !!filters.serviceType || !!filters.handledBy;
        const displayTotal = isClientSideFiltered ? filteredProfiles.length : (data.pagination?.total ?? filteredProfiles.length);
        setPagination((prev) => ({
          ...prev,
          total: displayTotal,
          pages: isClientSideFiltered ? Math.ceil(filteredProfiles.length / prev.limit) : (data.pagination?.pages ?? Math.ceil(filteredProfiles.length / prev.limit)),
        }));
      } else {
        toast.error("Failed to fetch customer profiles");
      }
    } catch (error) {
      console.error("Error fetching customer profiles:", error);
      toast.error("Failed to fetch customer profiles");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }));
  };

  const handleSearch = (search: string) => {
    setFilters((prev) => ({ ...prev, search }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (
    key: keyof CustomerProfileFilters,
    value: any
  ) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      search: "",
      isActive: undefined,
      hasQboCustomerId: undefined,
      hasTelephonyQueues: undefined,
      queueName: undefined,
      serviceType: undefined,
      handledBy: undefined,
      reportTypes: undefined,
    });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchCustomerProfiles();
    setIsRefreshing(false);
  };

  const addCustomerProfile = async (
    profile: CustomerProfilePayload
  ): Promise<SaveResult> => {
    try {
      const response = await fetch("/api/v1/customer-profiles", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });

      if (response.ok) {
        toast.success("Customer profile created successfully");
        fetchCustomerProfiles();
        return { success: true };
      } else {
        const error = await response.json();
        const message =
          error?.errors?.[0]?.msg ||
          error?.error?.message ||
          error?.error ||
          error?.message ||
          "Failed to create customer profile";
        return { success: false, errorMessage: message };
      }
    } catch (error) {
      console.error("Error creating customer profile:", error);
      return {
        success: false,
        errorMessage: "Failed to create customer profile",
      };
    }
  };

  const updateCustomerProfile = async (
    id: string,
    profile: CustomerProfilePayload
  ): Promise<SaveResult> => {
    try {
      const response = await fetch(`/api/v1/customer-profiles/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(profile),
      });

      if (response.ok) {
        toast.success("Customer profile updated successfully");
        fetchCustomerProfiles();
        return { success: true };
      } else {
        const error = await response.json();
        const message =
          error?.error?.message ||
          error?.error ||
          error?.message ||
          "Failed to update customer profile";
        return { success: false, errorMessage: message };
      }
    } catch (error) {
      console.error("Error updating customer profile:", error);
      return {
        success: false,
        errorMessage: "Failed to update customer profile",
      };
    }
  };

  const deleteCustomerProfile = async (id: string) => {
    if (!confirm("Are you sure you want to delete this customer profile?"))
      return;

    try {
      const response = await fetch(`/api/v1/customer-profiles/${id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        toast.success("Customer profile deleted successfully");
        fetchCustomerProfiles();
      } else {
        const error = await response.json();
        toast.error(error.message || "Failed to delete customer profile");
      }
    } catch (error) {
      console.error("Error deleting customer profile:", error);
      toast.error("Failed to delete customer profile");
    }
  };

  const handleRescheduleBilling = async (profileId: string) => {
    try {
      const response = await fetch(
        `/api/v1/customer-profiles/${profileId}/reschedule-billing`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reschedule billing");
      }

      const data = await response.json();
      toast.success(`Billing rescheduled for ${data.data.customerName}`);

      // Refresh the profiles list
      fetchCustomerProfiles();
    } catch (error) {
      console.error("Error rescheduling billing:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to reschedule billing"
      );
    }
  };

  const handleRescheduleAllBilling = async () => {
    try {
      const response = await fetch(
        "/api/v1/customer-profiles/reschedule-all-billing",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to reschedule all billing");
      }

      const data = await response.json();
      toast.success(
        `Rescheduled billing for ${data.data.successCount} customer profiles`
      );

      // Refresh the profiles list
      fetchCustomerProfiles();
    } catch (error) {
      console.error("Error rescheduling all billing:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to reschedule all billing"
      );
    }
  };

  const ServiceTypeBadge = ({ type }: { type?: string }) => {
    const map: Record<string, { label: string; cls: string }> = {
      inbound:    { label: "Inbound",       cls: "bg-blue-100 text-blue-700" },
      outbound:   { label: "Reactivations", cls: "bg-purple-100 text-purple-700" },
      both:       { label: "Both",          cls: "bg-teal-100 text-teal-700" },
      ai_admin:   { label: "AI - Admin",    cls: "bg-emerald-100 text-emerald-700" },
      ai_medical: { label: "AI - Medical",  cls: "bg-green-100 text-green-700" },
    };
    const config = map[type || "inbound"] ?? map["inbound"];
    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-0.5 ${config.cls}`}>
        {config.label}
      </span>
    );
  };

  const getStatusBadge = (profile: CustomerProfile) => {
    if (!profile.isActive) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          Inactive
        </span>
      );
    }
    if (!profile.qboCustomerId) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          No QBO Mapping
        </span>
      );
    }
    if (!profile.genesysQueueNames || profile.genesysQueueNames.length === 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          No Queues
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        Active
      </span>
    );
  };

  const getCompletionScore = (profile: CustomerProfile) => {
    let score = 0;
    let total = 0;

    // Basic profile information
    if (profile.qboDisplayName) {
      score++;
      total++;
    }
    if (profile.qboCompanyName) {
      score++;
      total++;
    }
    if (profile.qboGivenName) {
      score++;
      total++;
    }
    if (profile.qboFamilyName) {
      score++;
      total++;
    }
    if (profile.qboPrimaryEmailAddr) {
      score++;
      total++;
    }
    if (profile.qboPrimaryPhone) {
      score++;
      total++;
    }
    if (profile.qboLine1) {
      score++;
      total++;
    }
    if (profile.qboCity) {
      score++;
      total++;
    }
    if (profile.qboCountrySubDivisionCode) {
      score++;
      total++;
    }
    if (profile.qboPostalCode) {
      score++;
      total++;
    }
    if (profile.qboCountry) {
      score++;
      total++;
    }

    // Telephony integration
    if (profile.genesysQueueNames && profile.genesysQueueNames.length > 0) {
      score++;
      total++;
    }

    return total > 0 ? Math.round((score / total) * 100) : 0;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Customer Profiles
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage customer profiles and billing configurations
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <button
            onClick={handleRescheduleAllBilling}
            className="inline-flex items-center justify-center px-4 py-2 border border-yellow-300 shadow-sm text-sm font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Reschedule All Billing
          </button>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Customer Profile
          </button>
        </div>
      </div>

      {/* Quick Search */}
      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        <input
          type="text"
          placeholder="Search customers by name, email, or QBO ID…"
          value={filters.search}
          onChange={(e) => handleSearch(e.target.value)}
          className="w-full pl-11 pr-10 py-3 rounded-lg border border-gray-300 bg-white shadow-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        />
        {filters.search && (
          <button
            onClick={() => handleSearch("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Users className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Profiles
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {pagination.total}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Building2 className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Active Profiles
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customerProfiles.filter((p) => p.isActive).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Phone className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Total Queues
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customerProfiles.reduce(
                      (acc, p) => acc + (p.genesysQueueNames?.length || 0),
                      0
                    )}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CreditCard className="h-6 w-6 text-purple-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    QBO Mapped
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {customerProfiles.filter((p) => p.qboCustomerId).length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Edit className="h-6 w-6 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Complete Profiles
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {
                      customerProfiles.filter(
                        (p) => getCompletionScore(p) >= 80
                      ).length
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          <button
            onClick={clearFilters}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Clear Filters
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              value={
                filters.isActive === undefined
                  ? ""
                  : filters.isActive.toString()
              }
              onChange={(e) =>
                handleFilterChange(
                  "isActive",
                  e.target.value === "" ? undefined : e.target.value === "true"
                )
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              QBO Mapping
            </label>
            <select
              value={
                filters.hasQboCustomerId === undefined
                  ? ""
                  : filters.hasQboCustomerId.toString()
              }
              onChange={(e) =>
                handleFilterChange(
                  "hasQboCustomerId",
                  e.target.value === "" ? undefined : e.target.value === "true"
                )
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="true">Has QBO ID</option>
              <option value="false">No QBO ID</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Queue Status
            </label>
            <select
              value={
                filters.queueName === "active_no_queue"
                  ? "active_no_queue"
                  : filters.hasTelephonyQueues === undefined
                  ? ""
                  : filters.hasTelephonyQueues.toString()
              }
              onChange={(e) => {
                if (e.target.value === "active_no_queue") {
                  setFilters((prev) => ({ ...prev, hasTelephonyQueues: undefined, queueName: "active_no_queue" }));
                } else {
                  setFilters((prev) => ({
                    ...prev,
                    queueName: prev.queueName === "active_no_queue" ? undefined : prev.queueName,
                    hasTelephonyQueues: e.target.value === "" ? undefined : e.target.value === "true",
                  }));
                }
              }}
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="true">Has Queues</option>
              <option value="false">No Queues</option>
              <option value="active_no_queue">Active — No Queue Assigned</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Profile Completeness
            </label>
            <select
              value={filters.queueName || ""}
              onChange={(e) =>
                handleFilterChange("queueName", e.target.value || undefined)
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Profiles</option>
              <option value="complete">Complete (80%+)</option>
              <option value="partial">Partial (40-79%)</option>
              <option value="minimal">Minimal (0-39%)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Type
            </label>
            <select
              value={filters.serviceType || ""}
              onChange={(e) =>
                handleFilterChange("serviceType", e.target.value || undefined)
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All Types</option>
              <option value="inbound">Inbound</option>
              <option value="outbound">Outbound</option>
              <option value="both">Both</option>
              <option value="ai_admin">AI Admin</option>
              <option value="ai_medical">AI Medical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Handled By
            </label>
            <select
              value={filters.handledBy || ""}
              onChange={(e) =>
                handleFilterChange("handledBy", e.target.value || undefined)
              }
              className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">All</option>
              <option value="NR Medical">NR Medical</option>
              <option value="NR Admin">NR Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Report Type
            </label>
            <ReportTypeMultiSelect
              selected={filters.reportTypes || []}
              onChange={(values) =>
                handleFilterChange("reportTypes", values.length > 0 ? values : undefined)
              }
            />
          </div>
        </div>
      </div>

      {/* Customer Profiles Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-gray-900">
              Customer Profiles ({pagination.total})
            </h3>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10 shadow-sm">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50 z-20 border-r border-gray-200">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Aircall Queues
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  QBO Mapping
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Config
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rate/Min
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky right-0 bg-gray-50 z-20 border-l border-gray-200">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    Loading customer profiles...
                  </td>
                </tr>
              ) : customerProfiles.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No customer profiles found
                  </td>
                </tr>
              ) : (
                customerProfiles.map((profile) => {
                  const profileSchedulerLabel = getTimezoneDisplayName(
                    profile.billingTimezone || BILLING_SOURCE_TIMEZONE
                  );

                  return (
                    <tr key={profile.id} className="hover:bg-gray-50 group">
                      <td className="px-6 py-4 whitespace-nowrap sticky left-0 bg-white group-hover:bg-gray-50 z-10 border-r border-gray-200">
                        <div>
                          <div className="text-sm font-medium text-gray-900 flex items-center gap-0.5">
                          {profile.qboDisplayName}
                          {profile.notes && (
                            <span className="text-amber-500 font-bold leading-none" title="Has notes — click to expand">*</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">
                          {profile.qboPrimaryEmailAddr || "No email"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {profile.qboPrimaryPhone || "No phone"}
                        </div>
                        <div className="text-sm text-gray-500">
                          {profile.qboLine1
                            ? `${profile.qboLine1}, ${profile.qboCity || ""}, ${
                                profile.qboCountrySubDivisionCode || ""
                              } ${
                                profile.qboPostalCode
                                  ? `, ${profile.qboPostalCode}`
                                  : ""
                              } ${profile.qboCountry || ""}`
                            : "No address"}
                        </div>
                        {profile.qboCompanyName && (
                          <div className="text-xs text-blue-600 mt-1">
                            Company: {profile.qboCompanyName}
                          </div>
                        )}
                        {profile.qboGivenName && profile.qboFamilyName && (
                          <div className="text-xs text-gray-600 mt-1">
                            {profile.qboTitle && `${profile.qboTitle} `}
                            {profile.qboGivenName}{" "}
                            {profile.qboMiddleName &&
                              `${profile.qboMiddleName} `}
                            {profile.qboFamilyName}
                            {profile.qboSuffix && `, ${profile.qboSuffix}`}
                          </div>
                        )}
                        <div className="mt-1">
                          <div className="flex items-center group relative">
                            <div className="text-xs text-gray-400 mr-2">
                              Profile Complete:
                            </div>
                            <div className="w-16 bg-gray-200 rounded-full h-2">
                              <div
                                className="bg-primary-600 h-2 rounded-full"
                                style={{
                                  width: `${getCompletionScore(profile)}%`,
                                }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 ml-2">
                              {getCompletionScore(profile)}%
                            </span>

                            {/* Tooltip */}
                            <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-2 px-3 whitespace-nowrap z-10">
                              <div className="font-medium mb-1">
                                Profile Completion Details:
                              </div>
                              <div>
                                • QBO Display Name:{" "}
                                {profile.qboDisplayName ? "✓" : "✗"}
                              </div>
                              <div>
                                • QBO Company:{" "}
                                {profile.qboCompanyName ? "✓" : "✗"}
                              </div>
                              <div>
                                • QBO Personal:{" "}
                                {profile.qboGivenName ? "✓" : "✗"}{" "}
                                {profile.qboFamilyName ? "✓" : "✗"}
                              </div>
                              <div>
                                • QBO Contact:{" "}
                                {profile.qboPrimaryEmailAddr ? "✓" : "✗"}{" "}
                                {profile.qboPrimaryPhone ? "✓" : "✗"}
                              </div>
                              <div>
                                • QBO Address: {profile.qboLine1 ? "✓" : "✗"}{" "}
                                {profile.qboCity ? "✓" : "✗"}
                              </div>
                              <div>
                                • Aircall:{" "}
                                {profile.genesysQueueNames?.length > 0
                                  ? "✓"
                                  : "✗"}
                              </div>
                              <div className="mt-1 text-gray-300">
                                Hover for details
                              </div>
                            </div>
                          </div>
                        </div>
                        {/* Contract end date badge */}
                        {profile.contractEndDate && (() => {
                          const today = new Date().toISOString().slice(0, 10);
                          const ced = profile.contractEndDate;
                          const isPast = ced <= today;
                          const isUpcoming = !isPast && ced <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
                          return (
                            <div className="mt-1">
                              {isPast ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-red-50 text-red-700 text-xs font-medium border border-red-200">
                                  <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>
                                  Contract ended {ced}
                                </span>
                              ) : isUpcoming ? (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-orange-50 text-orange-700 text-xs font-medium border border-orange-200">
                                  <span className="w-2 h-2 rounded-full bg-orange-400 flex-shrink-0"></span>
                                  Ending {ced}
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium border border-blue-200">
                                  <span className="w-2 h-2 rounded-full bg-blue-400 flex-shrink-0"></span>
                                  Contract until {ced}
                                </span>
                              )}
                            </div>
                          );
                        })()}
                        {/* Forecast status */}
                        <div className="mt-2">
                          {(() => {
                            const fsd = profile.forecastStartDate;
                            if (!fsd) {
                              return (
                                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-gray-100 text-gray-500 text-xs font-medium border border-gray-200">
                                  <span className="w-2 h-2 rounded-full bg-gray-400 flex-shrink-0"></span>
                                  No Forecast Config
                                </span>
                              );
                            }
                            const today = new Date().toISOString().slice(0, 10);
                            const isPast = fsd <= today;
                            return isPast ? (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-green-50 text-green-700 text-xs font-medium border border-green-200">
                                <span className="w-2 h-2 rounded-full bg-green-500 flex-shrink-0"></span>
                                Forecast from {fsd}
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-xs font-medium border border-amber-200">
                                <span className="w-2 h-2 rounded-full bg-amber-400 flex-shrink-0"></span>
                                Estimates until {fsd}
                              </span>
                            );
                          })()}
                        </div>
                        {/* Collapsible notes section */}
                        {profile.notes && (
                          <div className="mt-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedNoteIds((prev) => {
                                  const next = new Set(prev);
                                  if (next.has(profile.id)) next.delete(profile.id);
                                  else next.add(profile.id);
                                  return next;
                                });
                              }}
                              className="flex items-center gap-1 text-xs text-amber-600 hover:text-amber-800 font-medium"
                            >
                              {expandedNoteIds.has(profile.id)
                                ? <ChevronDown className="h-3 w-3" />
                                : <ChevronRight className="h-3 w-3" />
                              }
                              Notes
                            </button>
                            {expandedNoteIds.has(profile.id) && (
                              <div className="mt-1 text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-2 whitespace-pre-wrap max-w-xs">
                                {profile.notes}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1">
                        {profile.genesysQueueNames &&
                        profile.genesysQueueNames.length > 0 ? (
                          profile.genesysQueueNames.map((queue, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                            >
                              <Phone className="h-3 w-3 mr-1" />
                              {queue}
                            </span>
                          ))
                        ) : (
                          <span className="text-sm text-gray-500">
                            No queues assigned
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {profile.qboCustomerId ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CreditCard className="h-3 w-3 mr-1" />
                            {profile.qboCustomerId}
                          </span>
                        ) : (
                          <span className="text-sm text-gray-500">
                            Not mapped
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500 mt-1">
                        {profile.qboDisplayName}
                      </div>
                      {profile.qboCompanyName &&
                        profile.qboCompanyName !== profile.qboDisplayName && (
                          <div className="text-xs text-blue-600 mt-1">
                            {profile.qboCompanyName}
                          </div>
                        )}
                      {profile.qboGivenName && profile.qboFamilyName && (
                        <div className="text-xs text-gray-600 mt-1">
                          {profile.qboTitle && `${profile.qboTitle} `}
                          {profile.qboGivenName}{" "}
                          {profile.qboMiddleName && `${profile.qboMiddleName} `}
                          {profile.qboFamilyName}
                          {profile.qboSuffix && `, ${profile.qboSuffix}`}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        <div className="font-medium text-gray-900">
                          Bill (local): Day {profile.billDate} at{" "}
                          {profile.billTimeFormatted || profile.billTime}
                        </div>
                        <div className="text-xs text-gray-500">
                          Configured ({profileSchedulerLabel}): Day{" "}
                          {profile.billDateBilling ?? profile.billDate} at{" "}
                          {profile.billTimeBillingFormatted ||
                            profile.billTimeBilling ||
                            profile.billTime}
                        </div>
                        <div className="text-xs text-gray-500">
                          Period: {profile.billingPeriodStart} -{" "}
                          {profile.billingPeriodEnd}
                        </div>
                        <div className="text-xs text-gray-500">
                          Min: {profile.minimumMinutes?.toLocaleString("en-US")} min
                        </div>
                        <div className="text-xs text-gray-500">
                          Overage: ${profile.overageRatePerMinute}/min
                        </div>
                        {profile.hasInvoiceDate && profile.invoiceDate && (
                          <div className="text-xs text-amber-600 mt-1">
                            Invoice: Day {profile.invoiceDate}
                          </div>
                        )}
                        {profile.billOveragesSeparately && profile.overageBillDate && (
                          <div className="text-xs text-blue-600 mt-1">
                            Overage Bill: Day {profile.overageBillDate}
                            {profile.overageHasInvoiceDate && profile.overageInvoiceDate && (
                              <span className="ml-1 text-amber-600">
                                · Invoice: Day {profile.overageInvoiceDate}
                              </span>
                            )}
                            {!!profile.overageBillOffsetDays && (
                              <span className="ml-1 text-emerald-600">
                                · Due +{profile.overageBillOffsetDays}d
                              </span>
                            )}
                          </div>
                        )}
                        {profile.weeklyOveragesEnabled && (
                          <div className="text-xs text-purple-600 mt-1">
                            Weekly overages (&gt;$750) · Sun +{profile.weeklyOverageBillOffsetDays ?? 5}d
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>${profile.ratePerMinute}</div>
                      <ServiceTypeBadge type={profile.serviceType} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(profile)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium sticky right-0 bg-white group-hover:bg-gray-50 z-10 border-l border-gray-200">
                      <div className="flex space-x-2">
                        <button
                          onClick={() => {
                            setEditingProfile(profile);
                            setShowModal(true);
                          }}
                          className="text-primary-600 hover:text-primary-900"
                          title="Edit Profile"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowStats(profile.id)}
                          className="text-blue-600 hover:text-blue-900"
                          title="View Stats"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setShowEmailSubscriptions(profile)}
                          className="text-purple-600 hover:text-purple-900"
                          title="Email Reports"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => deleteCustomerProfile(profile.id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete Profile"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleRescheduleBilling(profile.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                          title="Reschedule Billing"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                {Math.min(pagination.page * pagination.limit, pagination.total)}{" "}
                of {pagination.total} results
              </div>
              <div className="flex items-center space-x-2">
                <select
                  value={pagination.limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-gray-700">per page</span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-2 text-sm text-gray-700">
                  Page {pagination.page} of {pagination.pages}
                </span>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <CustomerProfileModal
          profile={editingProfile}
          onSave={
            editingProfile
              ? (profile) => updateCustomerProfile(editingProfile.id, profile)
              : addCustomerProfile
          }
          onClose={() => {
            setShowModal(false);
            setEditingProfile(null);
          }}
          onRescheduleBilling={handleRescheduleBilling}
        />
      )}

      {/* Stats Modal */}
      {showStats && (
        <StatsModal profileId={showStats} onClose={() => setShowStats(null)} />
      )}

      {/* Email Subscriptions Modal */}
      {showEmailSubscriptions && (
        <EmailSubscriptionsModal
          customerId={showEmailSubscriptions.id}
          customerName={showEmailSubscriptions.qboDisplayName || showEmailSubscriptions.qboCompanyName || "Customer"}
          customerEmail={showEmailSubscriptions.qboPrimaryEmailAddr}
          serviceType={showEmailSubscriptions.serviceType}
          onClose={() => setShowEmailSubscriptions(null)}
        />
      )}
    </div>
  );
}

// Customer Profile Modal Component
interface CustomerProfileModalProps {
  profile?: CustomerProfile | null;
  onSave: (profile: CustomerProfilePayload) => Promise<SaveResult>;
  onClose: () => void;
  onRescheduleBilling: (profileId: string) => Promise<void>;
}

function CustomerProfileModal({
  profile,
  onSave,
  onClose,
  onRescheduleBilling,
}: CustomerProfileModalProps) {
  const schedulerTimezoneLabel = getTimezoneDisplayName(
    profile?.billingTimezone || BILLING_SOURCE_TIMEZONE
  );
  const clientTimeZone = getUserTimeZone();
  const canonicalBillDate =
    profile?.billDateBilling ?? profile?.billDate ?? 1;
  const canonicalBillTime =
    profile?.billTimeBilling ?? profile?.billTime ?? "02:00";
  const localizedSchedule = getLocalizedScheduleInfo(
    canonicalBillDate,
    canonicalBillTime,
    profile?.billingTimezone || BILLING_SOURCE_TIMEZONE
  );

  const initialBillDate =
    profile?.billDate ??
    localizedSchedule?.localDay ??
    canonicalBillDate;
  const initialBillTime =
    profile?.billTime ??
    localizedSchedule?.localTime ??
    canonicalBillTime;

  const [formData, setFormData] = useState<CustomerProfileFormData>({
    qboCustomerId: profile?.qboCustomerId || "",
    genesysQueueNames: profile?.genesysQueueNames || [],
    isActive: profile?.isActive ?? true,
    serviceType: profile?.serviceType || "inbound",
    ratePerMinute:
      profile?.ratePerMinute !== undefined
        ? profile.ratePerMinute.toString()
        : "1.0",
    taxCode: profile?.taxCode || "",
    notes: profile?.notes || "",
    handledBy: profile?.handledBy ?? null,
    billDate: initialBillDate,
    billTime: initialBillTime,
    billingPeriodStart: profile?.billingPeriodStart || 1,
    billingPeriodEnd: profile?.billingPeriodEnd || 31,
    minimumMinutes: profile?.minimumMinutes || 0,
    overageRatePerMinute: profile?.overageRatePerMinute || 1.0,
    qboDisplayName: profile?.qboDisplayName || "",
    qboCompanyName: profile?.qboCompanyName || "",
    qboGivenName: profile?.qboGivenName || "",
    qboFamilyName: profile?.qboFamilyName || "",
    qboMiddleName: profile?.qboMiddleName || "",
    qboTitle: profile?.qboTitle || "",
    qboSuffix: profile?.qboSuffix || "",
    qboPrimaryEmailAddr: profile?.qboPrimaryEmailAddr || "",
    qboPrimaryPhone: profile?.qboPrimaryPhone || "",
    qboLine1: profile?.qboLine1 || "",
    qboCity: profile?.qboCity || "",
    qboCountrySubDivisionCode: profile?.qboCountrySubDivisionCode || "",
    qboPostalCode: profile?.qboPostalCode || "",
    qboCountry: profile?.qboCountry || "US",
    qboMetadata: profile?.qboMetadata || {},
    includeDisposition: profile?.includeDisposition ?? false,
    includeANI: profile?.includeANI ?? false,
    perCallReport: profile?.perCallReport ?? false,
    reportTimezone: profile?.reportTimezone || "America/Los_Angeles",
    officeSchedule: profile?.officeSchedule ?? null,
    forecastStartDate: profile?.forecastStartDate ?? null,
    contractEndDate: profile?.contractEndDate ?? null,
    projectionWindowStart: profile?.projectionWindowStart ?? null,
    projectionWindowEnd: profile?.projectionWindowEnd ?? null,
    weeklyEstimates: profile?.weeklyEstimates ?? null,
    reactivationBillDate: profile?.reactivationBillDate ?? null,
    reactivationMonthlyRate: profile?.reactivationMonthlyRate ?? null,
    reactivationPatientsCalledPerMonth: profile?.reactivationPatientsCalledPerMonth ?? null,
    aiMinimumDollar: profile?.aiMinimumDollar ?? null,
    initialPaymentDate: profile?.initialPaymentDate ?? null,
    initialPaymentAmount: (() => {
      if (profile?.initialPaymentAmount != null) return profile.initialPaymentAmount;
      const mins = profile?.minimumMinutes ?? 0;
      const rate = profile?.ratePerMinute ?? 0;
      return mins > 0 && rate > 0 ? parseFloat((mins * rate).toFixed(2)) : null;
    })(),
    goLiveDate: profile?.goLiveDate ?? null,
    billingStatus: profile?.billingStatus ?? "active",
    hasInvoiceDate: profile?.hasInvoiceDate ?? false,
    invoiceDate: profile?.invoiceDate ?? null,
    billOveragesSeparately: profile?.billOveragesSeparately ?? false,
    overageBillDate: profile?.overageBillDate ?? null,
    overageBillOffsetDays: profile?.overageBillOffsetDays ?? null,
    overageHasInvoiceDate: profile?.overageHasInvoiceDate ?? false,
    overageInvoiceDate: profile?.overageInvoiceDate ?? null,
    weeklyOveragesEnabled: profile?.weeklyOveragesEnabled ?? false,
    weeklyOverageBillOffsetDays: profile?.weeklyOverageBillOffsetDays ?? 5,
    retellAgentId: profile?.retellAgentId ?? null,
  });

  // Split minimumMinutes into whole (with comma formatting) and decimal parts
  const initMinStr = String(profile?.minimumMinutes ?? 0);
  const [initWholeStr, initDecStr] = initMinStr.split(".");
  const [minMinutesWhole, setMinMinutesWhole] = useState(
    (parseInt(initWholeStr, 10) || 0).toLocaleString("en-US")
  );
  const [minMinutesDecimal, setMinMinutesDecimal] = useState(initDecStr ?? "");

  const [newQueue, setNewQueue] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [queueValidationMessage, setQueueValidationMessage] = useState<string | null>(
    null
  );
  const [invalidQueues, setInvalidQueues] = useState<string[]>([]);
  const [aircallQueues, setAircallQueues] = useState<AircallQueueOption[]>([]);
  const [aircallQueuesLoading, setAircallQueuesLoading] = useState(false);
  const [aircallQueuesError, setAircallQueuesError] = useState<string | null>(
    null
  );
  const [unassignedQueueNames, setUnassignedQueueNames] = useState<Set<string> | null>(null);
  const [qboSearchResult, setQboSearchResult] = useState<any>(null);
  const [isSearchingQBO, setIsSearchingQBO] = useState(false);
  const [retellAgents, setRetellAgents] = useState<{ agent_id: string; agent_name: string }[]>([]);

  interface BillingAuditEntry {
    id: string;
    eventType: string;
    eventDate: string;
    notes: string | null;
    createdAt: string;
  }
  const [auditLog, setAuditLog] = useState<BillingAuditEntry[]>([]);

  useEffect(() => {
    if (!profile?.id) return;
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("authToken")}` };
    fetch(`/api/v1/customer-profiles/${profile.id}/billing-audit`, { headers: authHeader })
      .then((r) => r.ok ? r.json() : null)
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) setAuditLog(payload.data);
      })
      .catch(() => {/* non-critical */});
  }, [profile?.id]);

  useEffect(() => {
    const authHeader = { Authorization: `Bearer ${localStorage.getItem("authToken")}` };
    fetch("/api/v1/retell/agents", { headers: authHeader })
      .then((r) => r.ok ? r.json() : null)
      .then((payload) => {
        if (payload?.success && Array.isArray(payload.data)) setRetellAgents(payload.data);
      })
      .catch(() => {/* non-critical — agent list stays empty */});
  }, []);

  const normalizeQueue = (value: string) => value.trim().toLowerCase();
  const clearQueueValidation = () => {
    setQueueValidationMessage(null);
    setInvalidQueues([]);
  };

  const extractInvalidQueues = (message: string, queues: string[]) => {
    const matches = Array.from(message.matchAll(/"([^"]+)"/g)).map(
      (match) => match[1]
    );
    if (matches.length === 0) {
      return [];
    }
    const knownQueues = new Set(queues.map((queue) => normalizeQueue(queue)));
    return matches.filter((queue) => knownQueues.has(normalizeQueue(queue)));
  };

  const isQueueValidationMessage = (message: string) => {
    const lower = message.toLowerCase();
    return (
      (lower.includes("aircall") && lower.includes("queue")) ||
      lower.includes("queue name")
    );
  };

  useEffect(() => {
    let active = true;
    setAircallQueuesLoading(true);

    const authHeader = { Authorization: `Bearer ${localStorage.getItem("authToken")}` };

    Promise.all([
      fetchAircallQueues(),
      fetch("/api/v1/forecasting/queues?unassigned=true", { headers: authHeader })
        .then((r) => r.ok ? r.json() : null)
        .then((data) => {
          if (!data) return null;
          const names: string[] = data?.data?.unassignedQueues || [];
          return new Set(names.map((n) => n.toLowerCase()));
        })
        .catch(() => null),
    ])
      .then(([queues, unassigned]) => {
        if (!active) return;
        setAircallQueues(queues);
        setAircallQueuesError(null);
        setUnassignedQueueNames(unassigned);
      })
      .catch((error) => {
        if (!active) return;
        setAircallQueuesError(
          error instanceof Error
            ? error.message
            : "Unable to load Aircall queues"
        );
      })
      .finally(() => {
        if (active) setAircallQueuesLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const queueMatchMap = useMemo(() => {
    const map = new Map<string, string>();
    aircallQueues.forEach((queue) => {
      const nameKey = normalizeQueue(queue.name || "");
      if (nameKey) {
        map.set(nameKey, queue.name);
      }
      const idKey = normalizeQueue(queue.id || "");
      if (idKey) {
        map.set(idKey, queue.name);
      }
      if (queue.id) {
        const rawId = normalizeQueue(queue.id.replace(/^number:/, ""));
        if (rawId) {
          map.set(rawId, queue.name);
        }
      }
    });
    return map;
  }, [aircallQueues]);

  // Queues already on this customer (original profile) so they still appear in suggestions when editing
  const originalCustomerQueues = useMemo(() => new Set(
    (profile?.genesysQueueNames || []).map((n) => n.toLowerCase())
  ), [profile]);

  const queueSuggestions = useMemo(() => {
    const query = normalizeQueue(newQueue);
    if (!query) {
      return [];
    }
    return aircallQueues
      .filter((queue) => {
        const nameLower = queue.name.toLowerCase();
        // Only show unassigned queues (or queues already on this customer when editing)
        // null = still loading or failed → show all; Set = unassigned queue names
        const isAvailable = unassignedQueueNames === null
          || unassignedQueueNames.has(nameLower)
          || originalCustomerQueues.has(nameLower);
        if (!isAvailable) return false;
        const label = `${queue.name} ${queue.description || ""} ${queue.id || ""}`;
        return normalizeQueue(label).includes(query);
      })
      .slice(0, 8);
  }, [aircallQueues, newQueue, unassignedQueueNames, originalCustomerQueues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearQueueValidation();

    if (!formData.qboDisplayName.trim()) {
      toast.error("QBO Display Name is required for QuickBooks integration");
      return;
    }

    const isNonAircall = formData.serviceType === "outbound";
    const ratePerMinute = parseFloat(formData.ratePerMinute);
    if (!isNonAircall) {
      if (Number.isNaN(ratePerMinute)) {
        toast.error("Rate Per Minute must be a valid number");
        return;
      }
      if (ratePerMinute < 0) {
        toast.error("Rate Per Minute must be zero or greater");
        return;
      }
    }

    const { ratePerMinute: _, ...rest } = formData;
    const convertedSchedule = convertLocalScheduleToSource(
      formData.billDate,
      formData.billTime,
      BILLING_SOURCE_TIMEZONE,
      clientTimeZone
    );

    if (!convertedSchedule) {
      toast.error("Invalid billing time");
      return;
    }

    const payload: CustomerProfilePayload = {
      ...rest,
      ratePerMinute,
      billDate: formData.billDate,
      billTime: formData.billTime,
      clientTimeZone,
    };

    setIsSaving(true);
    try {
      const result = await onSave(payload);
      if (result.success) {
        onClose();
        return;
      }

      if (result.errorMessage) {
        if (isQueueValidationMessage(result.errorMessage)) {
          setQueueValidationMessage(result.errorMessage);
          setInvalidQueues(
            extractInvalidQueues(
              result.errorMessage,
              formData.genesysQueueNames
            )
          );
        } else {
          toast.error(result.errorMessage);
        }
      } else {
        toast.error("Failed to save customer profile");
      }
    } finally {
      setIsSaving(false);
    }
  };

  const resolveQueueSelection = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (aircallQueuesLoading || aircallQueuesError) {
      return null;
    }
    if (aircallQueues.length === 0) {
      return null;
    }
    return queueMatchMap.get(normalizeQueue(trimmed)) || null;
  };

  const addQueue = (value?: string) => {
    const resolved = resolveQueueSelection(value ?? newQueue);
    if (!resolved) {
      if (aircallQueuesLoading) {
        setQueueValidationMessage("Loading Aircall queues. Please wait.");
      } else if (aircallQueuesError) {
        setQueueValidationMessage(
          "Unable to load Aircall queues. Check the integration first."
        );
      } else {
        setQueueValidationMessage("Select a queue from the Aircall list");
      }
      return;
    }

    const normalized = normalizeQueue(resolved);
    const exists = formData.genesysQueueNames.some(
      (queue) => normalizeQueue(queue) === normalized
    );
    if (!exists) {
      setFormData((prev) => ({
        ...prev,
        genesysQueueNames: [...prev.genesysQueueNames, resolved],
      }));
    }
    clearQueueValidation();
    setNewQueue("");
  };

  const removeQueue = (queueToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      genesysQueueNames: prev.genesysQueueNames.filter(
        (q) => q !== queueToRemove
      ),
    }));
    setInvalidQueues((prev) => {
      const next = prev.filter(
        (queue) => normalizeQueue(queue) !== normalizeQueue(queueToRemove)
      );
      if (next.length === 0) {
        setQueueValidationMessage(null);
      }
      return next;
    });
  };

  const resolvedQueueInput = resolveQueueSelection(newQueue);
  const canAddQueue =
    !!resolvedQueueInput &&
    !formData.genesysQueueNames.some(
      (queue) => normalizeQueue(queue) === normalizeQueue(resolvedQueueInput)
    );
  const isQueuePickerLocked = aircallQueuesLoading || !!aircallQueuesError;

  const handleSearchQBOCustomer = async () => {
    if (!formData.qboDisplayName.trim()) {
      toast.error("Please enter a display name to search for");
      return;
    }

    setIsSearchingQBO(true);
    try {
      const response = await fetch(
        `/api/v1/qbo/customers/search/display-name?displayName=${encodeURIComponent(
          formData.qboDisplayName
        )}`
      );

      if (response.ok) {
        const result = await response.json();
        if (result.found) {
          setQboSearchResult(result.data);
          toast.success(`Found QBO customer: ${result.data.DisplayName}`);
        } else {
          setQboSearchResult(null);
          toast.success("No QBO customer found with that display name");
        }
      } else {
        const error = await response.json();
        toast.error(error.error || "Failed to search QBO customers");
      }
    } catch (error) {
      toast.error("Failed to search QBO customers");
      console.error("QBO search error:", error);
    } finally {
      setIsSearchingQBO(false);
    }
  };

  const handleConnectQBOCustomer = async (qboCustomerId: string) => {
    try {
      // Update the form data with the found QBO customer ID
      setFormData((prev) => ({
        ...prev,
        qboCustomerId,
      }));

      setQboSearchResult(null);
      toast.success("QBO Customer ID updated in form");
    } catch (error) {
      toast.error("Failed to connect QBO customer");
      console.error("QBO connection error:", error);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {profile ? "Edit Customer Profile" : "Add Customer Profile"}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 bg-gray-100 rounded-md p-1 hover:text-gray-600 hover:bg-gray-200 transition-colors duration-200"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-green-400 mb-3">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Display Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.qboDisplayName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboDisplayName: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Required for QBO integration"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Customer ID
                </label>
                <div className="flex items-center space-x-2">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.qboCustomerId}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          qboCustomerId: e.target.value,
                        }))
                      }
                      disabled={true}
                      className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                      placeholder="Optional"
                    />
                  </div>
                  {/* Manual QBO Customer Search */}
                  <div>
                    {qboSearchResult ? (
                      <button
                        type="button"
                        onClick={() =>
                          handleConnectQBOCustomer(qboSearchResult.Id)
                        }
                        className="mt-2 px-3 py-1 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                      >
                        Connect to This Customer
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={handleSearchQBOCustomer}
                        disabled={
                          !formData.qboDisplayName.trim() || isSearchingQBO
                        }
                        className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                      >
                        {isSearchingQBO
                          ? "Searching..."
                          : "Search QBO Customer ID"}
                      </button>
                    )}
                  </div>
                </div>
                {qboSearchResult && (
                  <p className="text-sm text-green-800 mt-2 text-right">
                    <strong>Found QBO Customer:</strong>{" "}
                    {qboSearchResult.DisplayName} (ID: {qboSearchResult.Id})
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Company Name
                </label>
                <input
                  type="text"
                  value={formData.qboCompanyName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboCompanyName: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Given Name
                </label>
                <input
                  type="text"
                  value={formData.qboGivenName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboGivenName: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional - First name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Family Name
                </label>
                <input
                  type="text"
                  value={formData.qboFamilyName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboFamilyName: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional - Last name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Middle Name
                </label>
                <input
                  type="text"
                  value={formData.qboMiddleName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboMiddleName: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Title
                </label>
                <input
                  type="text"
                  value={formData.qboTitle}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboTitle: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional - Mr, Mrs, Dr, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Suffix
                </label>
                <input
                  type="text"
                  value={formData.qboSuffix}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboSuffix: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional - Jr, Sr, III, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Primary Email
                </label>
                <input
                  type="email"
                  value={formData.qboPrimaryEmailAddr}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboPrimaryEmailAddr: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  QBO Primary Phone
                </label>
                <input
                  type="text"
                  value={formData.qboPrimaryPhone}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboPrimaryPhone: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional"
                />
              </div>

              {formData.serviceType !== "outbound" && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Rate Per Minute *
                </label>
                <input
                  type="text"
                  inputMode="decimal"
                  required
                  value={formData.ratePerMinute}
                  onChange={(e) => {
                    const { value } = e.target;
                    if (/^\d*(\.\d*)?$/.test(value) || value === "") {
                      setFormData((prev) => ({
                        ...prev,
                        ratePerMinute: value,
                      }));
                    }
                  }}
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Tax Code
                </label>
                <input
                  type="text"
                  value={formData.taxCode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      taxCode: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Optional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Service Type
                </label>
                <select
                  value={formData.serviceType}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      serviceType: e.target.value as CustomerProfile["serviceType"],
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                >
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Reactivations</option>
                  <option value="both">Both (Inbound + Reactivations)</option>
                  <option value="ai_admin">AI - Admin</option>
                  <option value="ai_medical">AI - Medical</option>
                </select>

                {(formData.serviceType === "ai_admin" || formData.serviceType === "ai_medical") && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700">
                      AI Agent (Retell)
                    </label>
                    <select
                      value={formData.retellAgentId || ""}
                      onChange={(e) => setFormData((prev) => ({ ...prev, retellAgentId: e.target.value || null }))}
                      className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                    >
                      <option value="">— select agent —</option>
                      {retellAgents.map((a) => (
                        <option key={a.agent_id} value={a.agent_id}>{a.agent_name}</option>
                      ))}
                    </select>
                    {retellAgents.length === 0 && (
                      <p className="mt-1 text-xs text-gray-400">No agents found — check RETELL_API_KEY</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Billing Configuration Fields */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-green-400 mb-3">
              Billing Configuration
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Bill Date (Day)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  value={formData.billDate}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billDate: parseInt(e.target.value, 10),
                    })
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Day of month to bill (1-31)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Bill Time
                </label>
                <input
                  type="time"
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  value={formData.billTime}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      billTime: e.target.value,
                    })
                  }
                />
                <p className="mt-1 text-xs text-gray-500">
                  Time of day to run billing (24-hour format, stored in{" "}
                  {schedulerTimezoneLabel})
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Billing Period Start (Day)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.billingPeriodStart}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      billingPeriodStart: parseInt(e.target.value, 10),
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Day billing period starts (1-31)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Billing Period End (Day)
                </label>
                <input
                  type="number"
                  min="1"
                  max="31"
                  value={formData.billingPeriodEnd}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      billingPeriodEnd: parseInt(e.target.value, 10),
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Day billing period ends (1-31)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Minimum Minutes
                </label>
                <div className="mt-1 flex items-center gap-1">
                  <input
                    type="text"
                    value={minMinutesWhole}
                    placeholder="0"
                    onChange={(e) => {
                      const raw = e.target.value.replace(/,/g, "").replace(/\D/g, "");
                      const num = raw ? parseInt(raw, 10) : 0;
                      const formatted = num.toLocaleString("en-US");
                      setMinMinutesWhole(formatted);
                      const dec = minMinutesDecimal ? parseFloat("0." + minMinutesDecimal) : 0;
                      setFormData((prev) => ({ ...prev, minimumMinutes: num + dec }));
                    }}
                    className="pl-2 w-full border border-gray-300 rounded-md h-8 shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                  />
                  <span className="text-gray-500 font-semibold text-lg select-none">.</span>
                  <input
                    type="text"
                    value={minMinutesDecimal}
                    placeholder="000"
                    maxLength={10}
                    onChange={(e) => {
                      const raw = e.target.value.replace(/\D/g, "");
                      setMinMinutesDecimal(raw);
                      const whole = parseInt(minMinutesWhole.replace(/,/g, ""), 10) || 0;
                      const dec = raw ? parseFloat("0." + raw) : 0;
                      setFormData((prev) => ({ ...prev, minimumMinutes: whole + dec }));
                    }}
                    className="pl-2 w-28 border border-gray-300 rounded-md h-8 shadow-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minutes included in base rate (e.g. 9,000 . 567 = 9000.567 min)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Overage Rate Per Minute
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.overageRatePerMinute}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      overageRatePerMinute: parseFloat(e.target.value),
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Rate for minutes over minimum
                </p>
              </div>
              {/* ── Has different invoice date ─────────────────────────── */}
              <div className="col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="hasInvoiceDate"
                    checked={formData.hasInvoiceDate ?? false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        hasInvoiceDate: e.target.checked,
                        invoiceDate: e.target.checked ? (prev.invoiceDate ?? null) : null,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <div>
                    <label htmlFor="hasInvoiceDate" className="block text-sm font-medium text-gray-700 cursor-pointer">
                      Has different invoice date
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Check if the invoice is sent on a different day than the card is charged. Bill Date above remains the charge date.
                    </p>
                  </div>
                </div>
                {formData.hasInvoiceDate && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Date (Day of Month)
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.invoiceDate ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          invoiceDate: e.target.value ? parseInt(e.target.value, 10) : null,
                        }))
                      }
                      className="mt-1 pl-2 block w-32 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                      placeholder="e.g. 5"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Day the QBO invoice is sent (placeholder — no automation yet)
                    </p>
                  </div>
                )}
              </div>

              {/* ── Bill overages separately ───────────────────────────── */}
              <div className="col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="billOveragesSeparately"
                    checked={formData.billOveragesSeparately ?? false}
                    disabled={formData.weeklyOveragesEnabled ?? false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        billOveragesSeparately: e.target.checked,
                        overageBillDate: e.target.checked ? (prev.overageBillDate ?? null) : null,
                        overageBillOffsetDays: e.target.checked ? (prev.overageBillOffsetDays ?? null) : null,
                        overageHasInvoiceDate: e.target.checked ? (prev.overageHasInvoiceDate ?? false) : false,
                        overageInvoiceDate: e.target.checked ? (prev.overageInvoiceDate ?? null) : null,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <div>
                    <label htmlFor="billOveragesSeparately" className="block text-sm font-medium text-gray-700 cursor-pointer">
                      Bill overages separately
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Check if overage charges are billed on a different date than the minimum/base charge.
                    </p>
                  </div>
                </div>
                {formData.billOveragesSeparately && (
                  <div className="mt-3 ml-7 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Overage Bill Date (Day of Month)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="31"
                        value={formData.overageBillDate ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            overageBillDate: e.target.value ? parseInt(e.target.value, 10) : null,
                          }))
                        }
                        className="mt-1 pl-2 block w-32 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="e.g. 15"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Day of month overages are charged (1–31)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Overage Due Offset (days after Overage Bill Date)
                      </label>
                      <input
                        type="number"
                        min="0"
                        max="180"
                        value={formData.overageBillOffsetDays ?? ""}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            overageBillOffsetDays: e.target.value ? parseInt(e.target.value, 10) : null,
                          }))
                        }
                        className="mt-1 pl-2 block w-32 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                        placeholder="e.g. 30"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Optional. If set, the overage becomes due this many days after the Overage Bill Date
                        (rolled to the next business day), instead of the same day. Leave blank for the normal behavior.
                      </p>
                    </div>
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        id="overageHasInvoiceDate"
                        checked={formData.overageHasInvoiceDate ?? false}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            overageHasInvoiceDate: e.target.checked,
                            overageInvoiceDate: e.target.checked ? (prev.overageInvoiceDate ?? null) : null,
                          }))
                        }
                        className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div>
                        <label htmlFor="overageHasInvoiceDate" className="block text-sm font-medium text-gray-700 cursor-pointer">
                          Has different invoice date for overages
                        </label>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Check if the overage invoice is sent on a different day than the overage charge date.
                        </p>
                      </div>
                    </div>
                    {formData.overageHasInvoiceDate && (
                      <div className="ml-7">
                        <label className="block text-sm font-medium text-gray-700">
                          Overage Invoice Date (Day of Month)
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="31"
                          value={formData.overageInvoiceDate ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              overageInvoiceDate: e.target.value ? parseInt(e.target.value, 10) : null,
                            }))
                          }
                          className="mt-1 pl-2 block w-32 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="e.g. 10"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Day the overage QBO invoice is sent (placeholder — no automation yet)
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* ── Weekly overages ────────────────────────────────────── */}
              <div className="col-span-2 border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="weeklyOveragesEnabled"
                    checked={formData.weeklyOveragesEnabled ?? false}
                    disabled={formData.billOveragesSeparately ?? false}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        weeklyOveragesEnabled: e.target.checked,
                        weeklyOverageBillOffsetDays: e.target.checked ? (prev.weeklyOverageBillOffsetDays ?? 5) : null,
                      }))
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500 disabled:opacity-50"
                  />
                  <div>
                    <label htmlFor="weeklyOveragesEnabled" className="block text-sm font-medium text-gray-700 cursor-pointer">
                      Weekly overages
                    </label>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Instead of one monthly overage bill, bill overage weekly (invoice date is always
                      Sunday) once accumulated overage exceeds $750. Mutually exclusive with "Bill
                      overages separately" above.
                    </p>
                  </div>
                </div>
                {formData.weeklyOveragesEnabled && (
                  <div className="mt-3 ml-7">
                    <label className="block text-sm font-medium text-gray-700">
                      Bill Date Offset (days after the Sunday invoice date)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={formData.weeklyOverageBillOffsetDays ?? ""}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          weeklyOverageBillOffsetDays: e.target.value ? parseInt(e.target.value, 10) : null,
                        }))
                      }
                      className="mt-1 pl-2 block w-32 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                      placeholder="e.g. 5"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      E.g. 5 = invoice Sunday, bill date the following Friday.
                    </p>
                  </div>
                )}
              </div>

              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="includeDisposition"
                  checked={formData.includeDisposition ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      includeDisposition: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <label
                    htmlFor="includeDisposition"
                    className="block text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Include Disposition in Excel Report
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, call disposition (tags) will appear as a column in the email's Excel attachment
                  </p>
                </div>
              </div>
                            <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="includeANI"
                  checked={formData.includeANI ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      includeANI: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <label
                    htmlFor="includeANI"
                    className="block text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Include ANI in Excel Reprot
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    When enabled, ANI's will appear as a column in the email's Excel attachment
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3 pt-2">
                <input
                  type="checkbox"
                  id="perCallReport"
                  checked={formData.perCallReport ?? false}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      perCallReport: e.target.checked,
                    }))
                  }
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <div>
                  <label
                    htmlFor="perCallReport"
                    className="block text-sm font-medium text-gray-700 cursor-pointer"
                  >
                    Send Report After Every Call
                  </label>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Enables automatic per-call phone log emails. Recipients are configured on per-call subscriptions (envelope icon). Add multiple subscriptions to send to multiple addresses.
                  </p>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Report Timezone
                </label>
                <select
                  value={formData.reportTimezone || "America/Los_Angeles"}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      reportTimezone: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                >
                  <option value="America/New_York">ET — Eastern Time</option>
                  <option value="America/Chicago">CT — Central Time</option>
                  <option value="America/Denver">MT — Mountain Time</option>
                  <option value="America/Los_Angeles">PT — Pacific Time</option>
                  <option value="America/Anchorage">AKT — Alaska Time</option>
                  <option value="Pacific/Honolulu">HAT — Hawaii Time</option>
                  <option value="America/Halifax">AST — Atlantic Time</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  "Time of Call" in the Excel attachment will display in this timezone
                </p>
              </div>
            </div>
          </div>

          {/* Reactivations Service Configuration */}
          {(formData.serviceType === "outbound" || formData.serviceType === "both") && (
            <div className="border-t pt-4">
              <h4 className="text-lg font-medium text-purple-500 mb-1">Reactivations Service</h4>
              <p className="text-xs text-gray-500 mb-3">
                Flat monthly rate for the patient reactivations service (separate from Aircall).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Bill Date (Day of Month)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.reactivationBillDate ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reactivationBillDate: e.target.value ? parseInt(e.target.value, 10) : null,
                      }))
                    }
                    className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g. 1"
                  />
                  <p className="text-xs text-gray-500 mt-1">Day the reactivations invoice is due</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Monthly Rate ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.reactivationMonthlyRate ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reactivationMonthlyRate: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g. 2500.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Fixed monthly dollar amount</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Patients Called / Month
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.reactivationPatientsCalledPerMonth ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reactivationPatientsCalledPerMonth: e.target.value ? parseInt(e.target.value, 10) : null,
                      }))
                    }
                    className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g. 500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Approximate patient count per month</p>
                </div>
              </div>
            </div>
          )}

          {(formData.serviceType === "ai_admin" || formData.serviceType === "ai_medical") && (
            <div className="border-t pt-4">
              <h4 className="text-lg font-medium text-emerald-500 mb-1">AI Service</h4>
              <p className="text-xs text-gray-500 mb-3">
                Billing fields for Retell AI calls. Rate Per Minute is set above. Dollar Minimum is the minimum charge per billing period (e.g. a package floor).
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Dollar Amount Minimum ($)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.aiMinimumDollar ?? ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        aiMinimumDollar: e.target.value ? parseFloat(e.target.value) : null,
                      }))
                    }
                    className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="e.g. 500.00"
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum monthly charge regardless of usage</p>
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Billing */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-amber-500 mb-1">Onboarding Billing</h4>
            <p className="text-xs text-gray-500 mb-3">
              Track the initial payment and go-live date. The system will automatically finalize the recurring bill date after the 1-month anniversary of the initial payment.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Initial Payment Date</label>
                <input
                  type="date"
                  value={formData.initialPaymentDate ?? ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, initialPaymentDate: e.target.value || null }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Day the client signed and paid their deposit</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Initial Payment Amount ($)</label>
                {(() => {
                  const calcAmt = (() => {
                    const mins = formData.minimumMinutes ?? 0;
                    const rate = parseFloat(formData.ratePerMinute) || 0;
                    return mins > 0 && rate > 0 ? parseFloat((mins * rate).toFixed(2)) : null;
                  })();
                  return (
                    <>
                      <div className="mt-1 flex items-center gap-2">
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={formData.initialPaymentAmount ?? ""}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, initialPaymentAmount: e.target.value ? parseFloat(e.target.value) : null }))
                          }
                          className="pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                          placeholder={calcAmt != null ? calcAmt.toFixed(2) : "e.g. 1500.00"}
                        />
                        {calcAmt != null && (formData.initialPaymentAmount ?? null) !== calcAmt && (
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, initialPaymentAmount: calcAmt }))}
                            className="shrink-0 text-xs px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100"
                            title="Use min minutes × rate per minute"
                          >
                            Use ${calcAmt.toFixed(2)}
                          </button>
                        )}
                      </div>
                      {calcAmt != null && (
                        <p className="text-xs text-gray-400 mt-1">
                          Calculated: {formData.minimumMinutes?.toLocaleString("en-US")} min × ${parseFloat(formData.ratePerMinute || "0").toFixed(4)}/min = <span className="font-medium text-gray-600">${calcAmt.toFixed(2)}</span>
                        </p>
                      )}
                      {calcAmt == null && (
                        <p className="text-xs text-gray-500 mt-1">Set Minimum Minutes and Rate Per Minute above to auto-calculate</p>
                      )}
                    </>
                  );
                })()}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Go-Live Date</label>
                <input
                  type="date"
                  value={formData.goLiveDate ?? ""}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, goLiveDate: e.target.value || null }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
                <p className="text-xs text-gray-500 mt-1">Day service started and calls began flowing</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Billing Status</label>
                <div className="mt-1 flex items-center gap-3">
                  <select
                    value={formData.billingStatus ?? "active"}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, billingStatus: e.target.value as "pending" | "active" }))
                    }
                    className="pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="active">Active</option>
                    <option value="pending">Pending</option>
                  </select>
                  {(formData.billingStatus ?? "active") === "pending" ? (
                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                      Pending
                    </span>
                  ) : (
                    <span className="shrink-0 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">Pending = awaiting 1-month anniversary check; Active = bill date finalized</p>
              </div>
            </div>

            {/* Audit Timeline — only shown when editing */}
            {profile && auditLog.length > 0 && (
              <div className="mt-4">
                <h5 className="text-sm font-semibold text-gray-700 mb-2">Billing History</h5>
                <ol className="relative border-l border-gray-200 space-y-3 ml-3">
                  {auditLog.map((entry) => {
                    const eventLabels: Record<string, { label: string; color: string }> = {
                      initial_payment:       { label: "Initial Payment",          color: "bg-blue-500" },
                      go_live:               { label: "Go-Live",                  color: "bg-green-500" },
                      minimum_check_passed:  { label: "Minimum Met",              color: "bg-emerald-500" },
                      minimum_check_failed:  { label: "Minimum Not Met",          color: "bg-amber-500" },
                      bill_date_finalized:   { label: "Bill Date Finalized",      color: "bg-purple-500" },
                    };
                    const cfg = eventLabels[entry.eventType] ?? { label: entry.eventType, color: "bg-gray-400" };
                    return (
                      <li key={entry.id} className="ml-4">
                        <div className={`absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full border border-white ${cfg.color}`} />
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-gray-700">{cfg.label}</span>
                          <span className="text-xs text-gray-400">{entry.eventDate}</span>
                        </div>
                        {entry.notes && (
                          <p className="text-xs text-gray-500 mt-0.5">{entry.notes}</p>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </div>
            )}
          </div>

          {/* Reschedule Billing Button - Only show when editing */}
          {profile && (
            <div className="mt-4 pt-4 border-t">
              <div className="flex items-center justify-between">
                <div>
                  <h5 className="text-sm font-medium text-gray-700">
                    Billing Schedule
                  </h5>
                  {localizedSchedule ? (
                    <>
                      <p className="text-xs text-gray-500">
                        Local: Day {localizedSchedule.localDay} at{" "}
                        {localizedSchedule.localTime} (
                        {localizedSchedule.localTimeZone})
                      </p>
                      <p className="text-xs text-gray-500">
                        Configured ({schedulerTimezoneLabel}): Day{" "}
                        {profile.billDate} at {profile.billTime}
                      </p>
                    </>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Configured ({schedulerTimezoneLabel}): Day{" "}
                      {profile.billDate} at {profile.billTime}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRescheduleBilling(profile.id)}
                  className="inline-flex items-center px-3 py-2 border border-yellow-300 shadow-sm text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-50 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Reschedule Now
                </button>
              </div>
            </div>
          )}

          {/* QBO Address Fields */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-green-400 mb-3">
              QuickBooks Address Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Street Address (Line 1)
                </label>
                <input
                  type="text"
                  value={formData.qboLine1}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboLine1: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Street address"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  City
                </label>
                <input
                  type="text"
                  value={formData.qboCity}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboCity: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  State/Province
                </label>
                <input
                  type="text"
                  value={formData.qboCountrySubDivisionCode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboCountrySubDivisionCode: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="State or province code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Postal Code
                </label>
                <input
                  type="text"
                  value={formData.qboPostalCode}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboPostalCode: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Postal code"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.qboCountry}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      qboCountry: e.target.value,
                    }))
                  }
                  className="mt-1 pl-2 block w-full border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  placeholder="Country code (e.g., US, CA)"
                />
              </div>
            </div>
          </div>

          {formData.serviceType !== "outbound" && formData.serviceType !== "ai_admin" && formData.serviceType !== "ai_medical" && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Aircall Queue Names *
            </label>
            {aircallQueuesLoading && (
              <div className="mt-1 flex items-center text-xs text-blue-600">
                <Loader2 className="h-3 w-3 animate-spin mr-2" />
                Loading Aircall queues...
              </div>
            )}
            <div className="mt-1 flex space-x-2">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={newQueue}
                  onChange={(e) => setNewQueue(e.target.value)}
                  placeholder={
                    aircallQueuesLoading
                      ? "Loading Aircall queues..."
                      : "Start typing to search Aircall queues"
                  }
                  className={`w-full pl-2 border rounded-md h-8 shadow-sm focus:outline-none ${
                    queueValidationMessage
                      ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                      : "border-gray-300 focus:ring-gray-500 focus:border-gray-500"
                  }`}
                  disabled={isQueuePickerLocked}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addQueue();
                    }
                  }}
                />
                {!isQueuePickerLocked && queueSuggestions.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg max-h-48 overflow-y-auto">
                    {queueSuggestions.map((queue) => (
                      <button
                        key={queue.id}
                        type="button"
                        onMouseDown={() => addQueue(queue.name)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
                      >
                        <div className="font-medium text-gray-900">
                          {queue.name}
                        </div>
                        {queue.description && (
                          <div className="text-xs text-gray-500">
                            {queue.description}
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => addQueue()}
                disabled={!canAddQueue}
                className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {aircallQueuesLoading ? (
                  <span className="inline-flex items-center">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Loading
                  </span>
                ) : (
                  "Add"
                )}
              </button>
            </div>
            {aircallQueuesError && (
              <p className="mt-1 text-xs text-amber-600">
                {aircallQueuesError}
              </p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {formData.genesysQueueNames.map((queue, index) => (
                <span
                  key={index}
                  className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    invalidQueues.some(
                      (invalid) => normalizeQueue(invalid) === normalizeQueue(queue)
                    )
                      ? "bg-red-100 text-red-800"
                      : "bg-blue-100 text-blue-800"
                  }`}
                >
                  {queue}
                  <button
                    type="button"
                    onClick={() => removeQueue(queue)}
                    className={`ml-2 ${
                      invalidQueues.some(
                        (invalid) => normalizeQueue(invalid) === normalizeQueue(queue)
                      )
                        ? "text-red-600 hover:text-red-800"
                        : "text-blue-600 hover:text-blue-800"
                    }`}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            {queueValidationMessage && (
              <p className="mt-2 text-sm text-red-600">
                {queueValidationMessage}
              </p>
            )}
          </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Handled By
            </label>
            <select
              value={formData.handledBy ?? ""}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  handledBy: (e.target.value || null) as "NR Admin" | "NR Medical" | null,
                }))
              }
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500 text-sm"
            >
              <option value="">— Not assigned —</option>
              <option value="NR Admin">NR Admin</option>
              <option value="NR Medical">NR Medical</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, notes: e.target.value }))
              }
              rows={3}
              className="mt-1 pl-2 block w-full border-gray-300 rounded-md shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
              placeholder="Optional notes about this customer"
            />
          </div>

          {/* Forecasting Setup */}
          <div className="border-t pt-4">
            <h4 className="text-lg font-medium text-green-400 mb-1">Forecasting Setup</h4>
            <p className="text-xs text-gray-500 mb-3">
              Set the date real call data starts for this account. If the date is in the future, you can provide estimated weekly volumes to use until real data is available.
            </p>
            <div className="space-y-4">
              {/* Step 1 — Date picker */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Data Start Date
                </label>
                <input
                  type="date"
                  value={formData.forecastStartDate ?? ""}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    setFormData((prev) => ({
                      ...prev,
                      forecastStartDate: val,
                    }));
                  }}
                  className="mt-1 pl-2 block w-48 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                />
              </div>

              {/* Contract End Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Contract End Date
                </label>
                <div className="flex items-center gap-3 mt-1">
                  <input
                    type="date"
                    value={formData.contractEndDate ?? ""}
                    onChange={(e) => {
                      const val = e.target.value || null;
                      setFormData((prev) => ({ ...prev, contractEndDate: val }));
                    }}
                    className="pl-2 block w-48 border-gray-300 rounded-md h-8 shadow-sm focus:outline-gray-500 focus:ring-gray-500 focus:border-gray-500"
                  />
                  {formData.contractEndDate && (
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, contractEndDate: null }))}
                      className="text-sm text-gray-400 hover:text-red-500"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  If set, this customer will be excluded from all projections after this date.
                </p>
              </div>

              {/* Step 2 — Weekly estimates (shown whenever a date is set) */}
              {!formData.forecastStartDate ? (
                <p className="text-xs text-gray-400 italic">
                  Enter a start date above to configure weekly estimates.
                </p>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Estimated Weekly Volume
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    {formData.forecastStartDate > new Date().toISOString().slice(0, 10)
                      ? <>These estimates will be used for all 8 weeks until <strong>{formData.forecastStartDate}</strong> when real call data takes over.</>
                      : <>Real call data is used from <strong>{formData.forecastStartDate}</strong>. Estimates below fill any weeks before that date which have no call history yet.</>
                    }
                  </p>
                  <div className="overflow-x-auto">
                    <table className="text-sm border border-gray-200 rounded-md">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-28">Day</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-36">Est. Calls Received</th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-36">Est. AHT (mins)</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(["monday","tuesday","wednesday","thursday","friday","saturday","sunday"] as const).map((day) => (
                          <tr key={day}>
                            <td className="px-3 py-2 text-gray-700 capitalize">{day}</td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={formData.weeklyEstimates?.[day]?.calls ?? ""}
                                onChange={(e) => {
                                  const calls = e.target.value === "" ? 0 : parseInt(e.target.value) || 0;
                                  setFormData((prev) => ({
                                    ...prev,
                                    weeklyEstimates: {
                                      monday:    { calls: 0, ahtMinutes: 0 },
                                      tuesday:   { calls: 0, ahtMinutes: 0 },
                                      wednesday: { calls: 0, ahtMinutes: 0 },
                                      thursday:  { calls: 0, ahtMinutes: 0 },
                                      friday:    { calls: 0, ahtMinutes: 0 },
                                      saturday:  { calls: 0, ahtMinutes: 0 },
                                      sunday:    { calls: 0, ahtMinutes: 0 },
                                      ...(prev.weeklyEstimates ?? {}),
                                      [day]: {
                                        calls,
                                        ahtMinutes: prev.weeklyEstimates?.[day]?.ahtMinutes ?? 0,
                                      },
                                    },
                                  }));
                                }}
                                className="w-24 pl-2 border border-gray-300 rounded h-7 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500"
                                placeholder="0"
                              />
                            </td>
                            <td className="px-3 py-2">
                              <input
                                type="number"
                                min="0"
                                step="0.1"
                                value={formData.weeklyEstimates?.[day]?.ahtMinutes ?? ""}
                                onChange={(e) => {
                                  const ahtMinutes = e.target.value === "" ? 0 : parseFloat(e.target.value) || 0;
                                  setFormData((prev) => ({
                                    ...prev,
                                    weeklyEstimates: {
                                      monday:    { calls: 0, ahtMinutes: 0 },
                                      tuesday:   { calls: 0, ahtMinutes: 0 },
                                      wednesday: { calls: 0, ahtMinutes: 0 },
                                      thursday:  { calls: 0, ahtMinutes: 0 },
                                      friday:    { calls: 0, ahtMinutes: 0 },
                                      saturday:  { calls: 0, ahtMinutes: 0 },
                                      sunday:    { calls: 0, ahtMinutes: 0 },
                                      ...(prev.weeklyEstimates ?? {}),
                                      [day]: {
                                        calls: prev.weeklyEstimates?.[day]?.calls ?? 0,
                                        ahtMinutes,
                                      },
                                    },
                                  }));
                                }}
                                className="w-24 pl-2 border border-gray-300 rounded h-7 text-sm focus:outline-none focus:ring-1 focus:ring-gray-500"
                                placeholder="0.0"
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Projection window */}
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Hiring Needs Projection Window
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Time range used to distribute estimated calls when no actual data exists. Default: 6:00 AM – 3:00 PM.
                </p>
                <div className="flex items-center gap-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Start time</label>
                    <input
                      type="time"
                      step="1800"
                      value={(() => {
                        const h = formData.projectionWindowStart ?? 6;
                        const hh = Math.floor(h);
                        const mm = Math.round((h - hh) * 60);
                        return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
                      })()}
                      onChange={(e) => {
                        if (!e.target.value) { setFormData((prev) => ({ ...prev, projectionWindowStart: null })); return; }
                        const [hh, mm] = e.target.value.split(":").map(Number);
                        setFormData((prev) => ({ ...prev, projectionWindowStart: hh + mm / 60 }));
                      }}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <span className="text-gray-400 mt-4">→</span>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">End time (exclusive)</label>
                    <input
                      type="time"
                      step="1800"
                      value={(() => {
                        const h = formData.projectionWindowEnd ?? 15;
                        const hh = h >= 24 ? 23 : Math.floor(h);
                        const mm = h >= 24 ? 59 : Math.round((h - hh) * 60);
                        return `${String(hh).padStart(2,"0")}:${String(mm).padStart(2,"0")}`;
                      })()}
                      onChange={(e) => {
                        if (!e.target.value) { setFormData((prev) => ({ ...prev, projectionWindowEnd: null })); return; }
                        const [hh, mm] = e.target.value.split(":").map(Number);
                        setFormData((prev) => ({ ...prev, projectionWindowEnd: hh + mm / 60 }));
                      }}
                      className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Call Forwarding Schedule ──────────────────────────────────── */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-lg font-medium text-green-400">Call Forwarding Schedule</h4>
              {formData.officeSchedule ? (
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, officeSchedule: null }))}
                  className="text-xs text-red-400 hover:text-red-600"
                >
                  Remove Schedule
                </button>
              ) : null}
            </div>
            <p className="text-xs text-gray-500 mb-3">
              When calls are forwarded to this office. Used on the Hiring Needs page beside the account name.
              Supports multiple windows per day (e.g. before and after lunch).
            </p>

            {!formData.officeSchedule ? (
              <button
                type="button"
                onClick={() => setFormData((prev) => ({ ...prev, officeSchedule: { ...EMPTY_OFFICE_SCHEDULE } }))}
                className="rounded-md border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-500 hover:border-green-400 hover:text-green-600 transition-colors"
              >
                + Configure Schedule
              </button>
            ) : (() => {
              const sched = formData.officeSchedule as OfficeSchedule;
              const mode  = sched.mode;

              const setMode = (m: ScheduleMode) =>
                setFormData((prev) => ({
                  ...prev,
                  officeSchedule: { ...(prev.officeSchedule as OfficeSchedule), mode: m },
                }));

              const setGlobalWindows = (windows: TimeWindow[]) =>
                setFormData((prev) => ({
                  ...prev,
                  officeSchedule: { ...(prev.officeSchedule as OfficeSchedule), global: windows },
                }));

              const setDayWindows = (day: OfficeDayKey, windows: TimeWindow[]) =>
                setFormData((prev) => {
                  const os = prev.officeSchedule as OfficeSchedule;
                  return { ...prev, officeSchedule: { ...os, perDay: { ...os.perDay, [day]: windows } } };
                });

              const WindowRow = ({
                windows, onChange,
              }: { windows: TimeWindow[]; onChange: (ws: TimeWindow[]) => void }) => (
                <div className="space-y-1">
                  {windows.map((w, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="time"
                        value={w.start}
                        onChange={(e) => {
                          const next = [...windows];
                          next[i] = { ...next[i], start: e.target.value };
                          onChange(next);
                        }}
                        className="rounded border border-gray-300 px-2 py-1 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-green-400"
                      />
                      <span className="text-gray-400 text-sm">–</span>
                      <input
                        type="time"
                        value={w.end}
                        onChange={(e) => {
                          const next = [...windows];
                          next[i] = { ...next[i], end: e.target.value };
                          onChange(next);
                        }}
                        className="rounded border border-gray-300 px-2 py-1 text-sm w-28 focus:outline-none focus:ring-1 focus:ring-green-400"
                      />
                      <button
                        type="button"
                        onClick={() => onChange(windows.filter((_, j) => j !== i))}
                        className="text-gray-300 hover:text-red-500 text-lg leading-none"
                        title="Remove window"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => onChange([...windows, { start: "08:00", end: "17:00" }])}
                    className="text-xs text-green-600 hover:text-green-800 mt-1"
                  >
                    + Add window
                  </button>
                </div>
              );

              return (
                <div className="space-y-4">
                  {/* Mode toggle */}
                  <div className="flex gap-2">
                    {(["always_on", "global", "per_day"] as ScheduleMode[]).map((m) => (
                      <button
                        key={m}
                        type="button"
                        onClick={() => setMode(m)}
                        className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                          mode === m
                            ? "bg-green-50 border-green-400 text-green-700"
                            : "border-gray-300 text-gray-500 hover:bg-gray-50"
                        }`}
                      >
                        {m === "always_on" ? "24/7 Always On" : m === "global" ? "Same Every Day" : "Per Day"}
                      </button>
                    ))}
                  </div>

                  {/* Always On */}
                  {mode === "always_on" && (
                    <p className="text-sm text-green-700 bg-green-50 rounded-md px-3 py-2 border border-green-200">
                      This office forwards calls 24 hours a day, 7 days a week.
                    </p>
                  )}

                  {/* Global — same windows every day */}
                  {mode === "global" && (
                    <div>
                      <p className="text-xs text-gray-500 mb-2">Same forwarding hours applied every day.</p>
                      <WindowRow windows={sched.global} onChange={setGlobalWindows} />
                    </div>
                  )}

                  {/* Per Day */}
                  {mode === "per_day" && (
                    <div className="overflow-x-auto">
                      <table className="text-sm border border-gray-200 rounded-md w-full">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 w-28">Day</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500">
                              Forwarding Windows <span className="font-normal text-gray-400">(add multiple for split shifts)</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {OFFICE_SCHEDULE_DAY_KEYS.map((day) => (
                            <tr key={day} className="align-top">
                              <td className="px-3 py-2 font-medium text-gray-700 whitespace-nowrap">
                                {OFFICE_SCHEDULE_DAY_LABELS[day]}
                              </td>
                              <td className="px-3 py-2">
                                <WindowRow
                                  windows={sched.perDay[day] || []}
                                  onChange={(ws) => setDayWindows(day, ws)}
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, isActive: e.target.checked }))
              }
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label
              htmlFor="isActive"
              className="ml-2 block text-sm text-gray-900"
            >
              Active
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <span className="inline-flex items-center">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  {profile ? "Updating..." : "Creating..."}
                </span>
              ) : profile ? (
                "Update"
              ) : (
                "Create"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Stats Modal Component
interface StatsModalProps {
  profileId: string;
  onClose: () => void;
}

function StatsModal({ profileId, onClose }: StatsModalProps) {
  const [stats, setStats] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [profileId]);

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `/api/v1/customer-profiles/${profileId}/stats`
      );
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">
            Customer Profile Statistics
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading statistics...</p>
          </div>
        ) : stats ? (
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  Total Calls
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.totalCalls}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  Total Minutes
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.totalMinutes}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  Total Amount
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  ${stats.totalAmount}
                </div>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  Avg Call Duration
                </div>
                <div className="text-2xl font-bold text-gray-900">
                  {stats.averageCallDuration} min
                </div>
              </div>
            </div>

            {stats.lastCallDate && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm font-medium text-gray-500">
                  Last Call Date
                </div>
                <div className="text-lg text-gray-900">
                  {new Date(stats.lastCallDate).toLocaleDateString()}
                </div>
              </div>
            )}

            {stats.queueUsage && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  Queue Usage Breakdown
                </h4>
                <div className="space-y-2">
                  {Object.entries(stats.queueUsage).map(
                    ([queueName, usage]: [string, any]) => (
                      <div
                        key={queueName}
                        className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
                      >
                        <span className="font-medium text-gray-900">
                          {queueName}
                        </span>
                        <div className="text-sm text-gray-600">
                          {usage.calls} calls • {usage.minutes} min • $
                          {usage.amount}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            )}

            {/* QBO Metadata Section */}
            {stats.qboMetadata && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  QuickBooks Integration Details
                </h4>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Company:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboCompanyName || "Not set"}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Display Name:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboDisplayName || "Not set"}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Primary Email:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboPrimaryEmailAddr || "Not set"}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Primary Phone:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboPrimaryPhone || "Not set"}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Personal Name:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboTitle &&
                          `${stats.qboMetadata.qboTitle} `}
                        {stats.qboMetadata.qboGivenName || ""}{" "}
                        {stats.qboMetadata.qboMiddleName &&
                          `${stats.qboMetadata.qboMiddleName} `}
                        {stats.qboMetadata.qboFamilyName || ""}
                        {stats.qboMetadata.qboSuffix &&
                          `, ${stats.qboMetadata.qboSuffix}`}
                      </div>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">
                        QBO Address:
                      </span>
                      <div className="text-gray-900">
                        {stats.qboMetadata.qboLine1
                          ? `${stats.qboMetadata.qboLine1}, ${
                              stats.qboMetadata.qboCity || ""
                            }, ${
                              stats.qboMetadata.qboCountrySubDivisionCode || ""
                            } ${stats.qboMetadata.qboPostalCode || ""} ${
                              stats.qboMetadata.qboCountry || ""
                            }`
                          : "Not set"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            No statistics available for this customer profile.
          </div>
        )}

        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
